<template>
  <div class="home-view">
    <div class="welcome-section">
      <h1>Welcome to Code, Ship, Watch</h1>
      <p class="subtitle">Choose a book to start your learning journey</p>
    </div>

    <div class="books-grid">
      <div 
        v-for="book in books" 
        :key="book.id" 
        class="book-card"
        :class="{ 'disabled': book.chapters.length === 0 }"
        @click="selectBook(book)"
      >
        <div class="book-header">
          <h2 class="book-title">{{ book.title }}</h2>
          <div class="book-progress" v-if="book.chapters.length > 0">
            <span class="progress-text">{{ getBookProgress(book) }}</span>
          </div>
        </div>
        
        <div class="book-content">
          <p class="book-description" v-if="book.description">
            {{ book.description }}
          </p>
          <div class="book-stats" v-if="book.chapters.length > 0">
            <div class="stat">
              <span class="stat-number">{{ book.chapters.length }}</span>
              <span class="stat-label">Chapters</span>
            </div>
            <div class="stat">
              <span class="stat-number">{{ getTotalLessons(book) }}</span>
              <span class="stat-label">Lessons</span>
            </div>
          </div>
          <div class="empty-state" v-else>
            <p>Coming Soon</p>
          </div>
        </div>

        <div class="book-footer" v-if="book.chapters.length > 0">
          <button class="start-button" @click.stop="startBook(book)">
            {{ getBookButtonText(book) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { books } from '../content/chapters'
import { computed, inject, ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getLastCompletedLesson, parseLessonKey, refreshUserProgress } from '../utils/progress'

export default {
  name: 'HomeView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const logger = inject('logger')
    const progressRefreshTrigger = ref(0)

    const selectBook = (book) => {
      if (book.chapters.length === 0) {
        logger.info('Book has no content yet:', { bookId: book.id, bookTitle: book.title })
        return
      }
      startBook(book)
    }

    const startBook = (book) => {
      logger.info('Starting book:', { bookId: book.id, bookTitle: book.title })
      router.push(`/book/${book.id}/chapter/1/lesson/1`)
    }

    const getTotalLessons = (book) => {
      return book.chapters.reduce((total, chapter) => total + chapter.lessons.length, 0)
    }

    const getBookProgress = (book) => {
      // Use the refresh trigger to force recomputation
      progressRefreshTrigger.value
      
      const lastCompleted = getLastCompletedLesson()
      if (!lastCompleted) return 'Not started'
      
      const completed = parseLessonKey(lastCompleted)
      if (!completed || completed.bookId !== book.id) return 'Not started'
      
      const totalLessons = getTotalLessons(book)
      const completedLessons = getCompletedLessonsInBook(book, completed)
      
      return `${completedLessons}/${totalLessons} lessons`
    }

    const getCompletedLessonsInBook = (book, completed) => {
      let count = 0
      for (const chapter of book.chapters) {
        if (completed.chapterId > chapter.id) {
          count += chapter.lessons.length
        } else if (completed.chapterId === chapter.id) {
          count += completed.lessonId
        }
      }
      return count
    }

    const getBookButtonText = (book) => {
      // Use the refresh trigger to force recomputation
      progressRefreshTrigger.value
      
      const lastCompleted = getLastCompletedLesson()
      if (!lastCompleted) return 'Start Book'
      
      const completed = parseLessonKey(lastCompleted)
      if (!completed || completed.bookId !== book.id) return 'Start Book'
      
      return 'Continue Book'
    }

    // Refresh progress when component is mounted or when route changes
    const refreshProgress = async () => {
      try {
        await refreshUserProgress()
        progressRefreshTrigger.value++
        logger.debug('HomeView: Progress refreshed')
      } catch (error) {
        logger.error('HomeView: Error refreshing progress', { error: error.message })
      }
    }

    onMounted(() => {
      refreshProgress()
    })

    // Watch for route changes to refresh progress when returning to home
    watch(() => route.path, (newPath, oldPath) => {
      if (newPath === '/' && oldPath && oldPath !== '/') {
        refreshProgress()
      }
    })

    return {
      books,
      selectBook,
      startBook,
      getTotalLessons,
      getBookProgress,
      getBookButtonText
    }
  }
}
</script>

<style scoped>
.home-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.welcome-section {
  text-align: center;
  margin-bottom: 3rem;
}

.welcome-section h1 {
  font-size: 2.5rem;
  color: var(--text);
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.subtitle {
  font-size: 1.2rem;
  color: var(--text-light);
  margin: 0;
}

.books-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.book-card {
  background: white;
  border: 2px solid #e1e8f5;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.book-card:hover:not(.disabled) {
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.book-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.book-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.book-title {
  font-size: 1.5rem;
  color: var(--text);
  margin: 0;
  font-weight: 600;
}

.book-progress {
  background: #f7fafc;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: var(--text-light);
}

.book-content {
  margin-bottom: 1.5rem;
}

.book-description {
  color: var(--text-light);
  margin-bottom: 1rem;
  line-height: 1.5;
}

.book-stats {
  display: flex;
  gap: 1.5rem;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
}

.stat-label {
  font-size: 0.85rem;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--text-light);
  font-style: italic;
}

.book-footer {
  display: flex;
  justify-content: center;
}

.start-button {
  background: var(--primary);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.start-button:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .home-view {
    padding: 1rem;
  }
  
  .welcome-section h1 {
    font-size: 2rem;
  }
  
  .books-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .book-stats {
    gap: 1rem;
  }
}
</style>
